import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { HelpCircle, Plus } from 'lucide-react'
import './Notices.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import MainURL from '../../MainURL'
import { drumtaleAdminSessionAtom, drumtaleAdminTokenAtom } from '../../AtomStore'
import { openPageSpecification } from '../shared/pageSpecification'
import {
  Notice,
  noticesDataModelGuide,
  noticesImplementationGuide,
  noticesSpecification,
  noticesStateDefinitions,
} from './noticesData'

const TOAST_DURATION_MS = 2400

type PeriodMode = 'day' | 'week' | 'month'

function toLocalDateText(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function matchesPeriod(value: string, start: string, end: string) {
  if (!value || value === '-') return false
  const dateText = value.slice(0, 10)
  return dateText >= (start || '1900-01-01') && dateText <= (end || '2999-12-31')
}

function noticeStatusBadgeType(status: string) {
  if (status === '노출중') return 'success'
  if (status === '예약') return 'primary'
  if (status === '종료됨') return ''
  return 'warning'
}

function Notices() {
  const navigate = useNavigate()
  const location = useLocation()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)
  const adminToken = useAtomValue(drumtaleAdminTokenAtom)

  const [noticeList, setNoticeList] = useState<Notice[]>([])
  const [isLoadingNotices, setIsLoadingNotices] = useState(false)
  const [periodStart, setPeriodStart] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  })
  const [periodEnd, setPeriodEnd] = useState(() => toLocalDateText(new Date()))

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  useEffect(() => {
    if (!isAdminSessionActive || !adminToken) return

    const loadNotices = async () => {
      setIsLoadingNotices(true)
      try {
        const response = await fetch(`${MainURL}/admin/notices`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        })
        const data = await response.json().catch(() => null)
        if (!response.ok) {
          showToast(data?.message || '공지 목록을 불러오지 못했습니다.')
          return
        }
        setNoticeList(data?.notices ?? [])
      } catch (error) {
        console.error('[Notices] fetch error:', error)
        showToast('공지 목록을 불러오지 못했습니다.')
      } finally {
        setIsLoadingNotices(false)
      }
    }

    loadNotices()
  }, [adminToken, isAdminSessionActive])

  useEffect(() => {
    const state = location.state as { newNotice?: Notice, updatedNotice?: Notice } | null
    const savedNotice = state?.updatedNotice || state?.newNotice
    if (!savedNotice) return
    showToast(state?.updatedNotice
      ? `"${savedNotice.title}" 공지를 수정했습니다.`
      : `"${savedNotice.title}" 공지를 등록했습니다.`)
    navigate(location.pathname, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const handlePeriodModeChange = (mode: PeriodMode) => {
    const today = new Date()
    const todayText = toLocalDateText(today)
    if (mode === 'week') {
      const weekAgo = new Date(today)
      weekAgo.setDate(today.getDate() - 6)
      setPeriodStart(toLocalDateText(weekAgo))
      setPeriodEnd(todayText)
    } else if (mode === 'day') {
      setPeriodStart(todayText)
      setPeriodEnd(todayText)
    } else {
      setPeriodStart(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`)
      setPeriodEnd(todayText)
    }
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(noticesSpecification, noticesDataModelGuide, noticesImplementationGuide, {
      stateDefinitions: noticesStateDefinitions,
    })
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  const filteredNotices = useMemo(() => {
    return noticeList.filter((notice) => matchesPeriod(notice.start, periodStart, periodEnd))
  }, [noticeList, periodStart, periodEnd])

  const handleDelete = async (id: string, title: string) => {
    if (!adminToken) return
    try {
      const response = await fetch(`${MainURL}/admin/notices/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '공지 삭제에 실패했습니다.')
        return
      }
      setNoticeList((previous) => previous.filter((notice) => notice.id !== id))
      showToast(`"${title}" 공지를 삭제했습니다.`)
    } catch (error) {
      console.error('[Notices] delete error:', error)
      showToast('공지 삭제에 실패했습니다.')
    }
  }

  return (
    <div className='Notices'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <h1>공지사항</h1>
            </div>
          </header>

          <section className='view active'>
            <section className='panel'>
              <div className='panel-header'>
                <div>
                  <h2>공지사항</h2>
                  <p>사용자에게 안내할 공지를 등록하고 수정합니다.</p>
                </div>
                <div className='button-row'>
                  <div className='period-range'>
                    <select className='filter period-filter' defaultValue='month' onChange={(event) => handlePeriodModeChange(event.target.value as PeriodMode)}>
                      <option value='day'>일간</option>
                      <option value='week'>주간</option>
                      <option value='month'>월간</option>
                    </select>
                    <input className='date-filter' type='date' value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
                    <span>~</span>
                    <input className='date-filter' type='date' value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
                  </div>
                  <button className='primary-button' type='button' onClick={() => navigate('/admin/notices/register')}>
                    <Plus size={15} />
                    공지 등록
                  </button>
                </div>
              </div>
              <div className='table-wrap'>
                <table>
                  <thead>
                    <tr>
                      <th>공지</th>
                      <th>상태</th>
                      <th>기간</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingNotices ? (
                      <tr><td colSpan={4}>공지 목록을 불러오는 중입니다.</td></tr>
                    ) : filteredNotices.length === 0 ? (
                      <tr><td colSpan={4}>검색 조건에 맞는 데이터가 없습니다.</td></tr>
                    ) : (
                      filteredNotices.map((notice) => (
                        <tr key={notice.id}>
                          <td>
                            <div className='notice-title-cell'>
                              <strong>{notice.title}</strong>
                              <small>{notice.body}</small>
                            </div>
                          </td>
                          <td><span className={`badge ${noticeStatusBadgeType(notice.status)}`}>{notice.status}</span></td>
                          <td>{notice.start} ~ {notice.end}</td>
                          <td className='row-actions'>
                            <button className='secondary-button' type='button' onClick={() => navigate(`/admin/notices/${notice.id}/edit`, { state: { notice } })}>수정</button>
                            <button className='secondary-button danger' type='button' onClick={() => handleDelete(notice.id, notice.title)}>삭제</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default Notices
